import type { ClientAccountProfile } from "@noverik/contracts";
import {
  Building2,
  CheckCircle2,
  KeyRound,
  LockKeyhole,
  Mail,
  Phone,
  ShieldCheck,
  UserRound,
} from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { apiRequest, ApiRequestError } from "../lib/api";

export function AccountPage() {
  const { session } = useAuth();
  const [profile, setProfile] = useState<ClientAccountProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    apiRequest<ClientAccountProfile>("/client/account")
      .then((payload) => {
        if (active) setProfile(payload);
      })
      .catch((err) => {
        if (!active) return;
        setError(
          err instanceof ApiRequestError
            ? err.message
            : "Não foi possível carregar sua conta.",
        );
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const changePassword = async (event: FormEvent) => {
    event.preventDefault();

    if (!session?.csrfToken) {
      setError("Sua sessão expirou. Atualize a página.");
      return;
    }

    if (newPassword !== confirmation) {
      setError("A confirmação da nova senha não confere.");
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await apiRequest<{
        changed: boolean;
        profile: ClientAccountProfile;
      }>("/client/account/password", {
        method: "POST",
        csrfToken: session.csrfToken,
        body: {
          currentPassword,
          newPassword,
          confirmation,
        },
      });

      setProfile(result.profile);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmation("");
      setSuccess("Senha alterada com sucesso.");
    } catch (err) {
      setError(
        err instanceof ApiRequestError
          ? err.message
          : "Não foi possível alterar sua senha.",
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="page"><div className="data-message">Carregando sua conta...</div></div>;
  }

  return (
    <div className="page account-page">
      <div className="page-heading">
        <div>
          <span className="eyebrow">MINHA CONTA</span>
          <h1>Perfil e segurança</h1>
          <p>Consulte seus dados de atendimento e gerencie suas credenciais de acesso.</p>
        </div>
      </div>

      {error && <div className="login-message form-error account-alert">{error}</div>}
      {success && (
        <div className="account-success">
          <CheckCircle2 size={17} />
          {success}
        </div>
      )}

      {profile && (
        <div className="account-grid">
          <section className="account-card profile-card">
            <div className="account-card-heading">
              <span className="meta-icon blue"><UserRound size={20} /></span>
              <div>
                <strong>Dados cadastrais</strong>
                <span>Informações utilizadas nos seus chamados.</span>
              </div>
            </div>

            <div className="account-identity">
              <div className="avatar account-avatar">
                {profile.name
                  .split(/\s+/)
                  .filter(Boolean)
                  .slice(0, 2)
                  .map((part) => part[0]?.toUpperCase())
                  .join("")}
              </div>
              <div>
                <strong>{profile.name}</strong>
                <span>{profile.account?.status || "Cliente"}</span>
              </div>
            </div>

            <div className="account-details">
              <div>
                <Mail size={16} />
                <span><small>E-mail</small><strong>{profile.email}</strong></span>
              </div>
              <div>
                <Phone size={16} />
                <span><small>Telefone</small><strong>{profile.phone || "Não informado"}</strong></span>
              </div>
              <div>
                <Building2 size={16} />
                <span><small>Organização</small><strong>{profile.organization?.name || "Sem organização"}</strong></span>
              </div>
              <div>
                <ShieldCheck size={16} />
                <span><small>Conta</small><strong>{profile.account?.active ? "Ativa" : "Verificar cadastro"}</strong></span>
              </div>
            </div>
          </section>

          <section className="account-card security-card">
            <div className="account-card-heading">
              <span className="meta-icon purple"><LockKeyhole size={20} /></span>
              <div>
                <strong>Segurança</strong>
                <span>Altere sua senha usando as mesmas regras configuradas no osTicket.</span>
              </div>
            </div>

            {profile.account?.passwordChangeEnabled ? (
              <form className="password-form" onSubmit={changePassword}>
                <label className="field">
                  <span>Senha atual</span>
                  <input
                    type="password"
                    autoComplete="current-password"
                    value={currentPassword}
                    onChange={(event) => setCurrentPassword(event.target.value)}
                    required
                  />
                </label>

                <label className="field">
                  <span>Nova senha</span>
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={(event) => setNewPassword(event.target.value)}
                    required
                  />
                </label>

                <label className="field">
                  <span>Confirmar nova senha</span>
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={confirmation}
                    onChange={(event) => setConfirmation(event.target.value)}
                    required
                  />
                </label>

                <button className="button primary" disabled={saving}>
                  <KeyRound size={16} />
                  {saving ? "Alterando..." : "Alterar senha"}
                </button>
              </form>
            ) : (
              <div className="account-disabled">
                A alteração de senha está desativada para esta conta.
              </div>
            )}

            <div className="account-preferences">
              <div><small>Fuso horário</small><strong>{profile.account?.timezone || "Padrão do sistema"}</strong></div>
              <div><small>Idioma</small><strong>{profile.account?.language || "Preferência do navegador"}</strong></div>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
